// Still composition of the people area (SA B9, US7 "thấy gì lưu nấy").
// Canvas2D version of render/stage.ts: same placement (layout.placeAll), the
// SAME layer order/arm decisions captured at the shutter (Decision), the same
// part masks (parts.ts capsules / head ellipse), the same Reinhard colour
// matching (color.ts), contact + ground shadows and light wrap. DOM-free: runs
// in the browser (room previews) and on the Node render worker (@napi-rs/canvas).
//
// Mirror convention: placement is computed in un-mirrored stage space exactly as
// live; the people area is then flipped iff `mirrorPhoto` (default true = as seen
// in the mirror). The background, frame and text are never flipped.

import { makeCanvas, type AnyCanvas, type Ctx2D } from './canvas';
import { applyXform, colorPlan, reinhardXform } from './color';
import {
  anchorBottom, applyView, placeAll, placementMatrix, slotX, stageXOf, toStage, type FaceBox, type PlaceInput, type Placement, type PoseAdjust,
} from './layout';
import { anchorOf, armGeometry, handPoint, headEllipse, shoulderWidth, solverPerson, type ArmGeom } from './parts';
import { KP, kpV, kpX, kpY, type PoseFrame, type Side } from './pose';
import { LAYER_KINDS, armsFor, defaultLayout, orderFor, solveOnce, type Decision, type LayerRef } from './solver';

type Img = CanvasImageSource & { width: number; height: number };

export interface StillPerson {
  image: Img;
  face: FaceBox;
  adjust: PoseAdjust;
  slot: number;
  noFace?: boolean;
  /** peer id (decision keys); defaults to `slot:<n>` */
  id?: string;
  pose?: PoseFrame | null;
  decision?: Decision | null;
}

/** light wrap over a drawn backdrop: subtle (v0.9.1: 0.2 tinted hair and shoulders with the backdrop colour) */
export const WRAP = 0.08;
export const CONTACT_SHADOW = 0.24;

const idOf = (p: StillPerson) => p.id ?? `slot:${p.slot}`;

/** The decision used for a shot: the one captured on the lowest slot (the host) wins, deterministically. */
export function shotDecision(people: StillPerson[]): Decision | null {
  return [...people].sort((a, b) => a.slot - b.slot).find((p) => p.decision)?.decision ?? null;
}

function capsulePath(ctx: Ctx2D, g: ArmGeom) {
  ctx.lineCap = 'round';
  const seg = (ax: number, ay: number, bx: number, by: number, r: number) => {
    ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx + 1e-3, by); ctx.lineWidth = 2 * r; ctx.stroke();
  };
  seg(g.upper.ax, g.upper.ay, g.upper.bx, g.upper.by, g.upper.r);
  if (g.fore) seg(g.fore.ax, g.fore.ay, g.fore.bx, g.fore.by, g.fore.r);
  if (g.hand) { ctx.beginPath(); ctx.arc(g.hand.x, g.hand.y, g.hand.r, 0, Math.PI * 2); ctx.fill(); }
}

/**
 * Where each person of a shot goes (cell w×h): the same placement as live, frozen at the shutter.
 * `matrix(i)` maps person i's source pixels to cell pixels (already mirrored for the photo).
 */
export function shotLayout(w: number, h: number, people: StillPerson[], sceneSlot: number | null, mirrorPhotoOpt?: boolean) {
  const d = shotDecision(people);
  const mirror = d?.mirror ?? true; // the live stage's display convention at the shutter
  const mirrorPhoto = mirrorPhotoOpt ?? d?.mirrorPhoto ?? true;
  const scene = sceneSlot == null ? null : people.find((p) => p.slot === sceneSlot) ?? null;
  const others = people.filter((p) => p !== scene).sort((a, b) => a.slot - b.slot);
  const legacy = defaultLayout(others.map(idOf));
  const inputs: PlaceInput[] = people.map((p) => {
    const slot = (d?.slots ?? legacy.slots).find((s) => s.peerId === idOf(p));
    const dispX = slot ? slot.x : slotX(Math.max(0, others.indexOf(p)), Math.max(1, others.length));
    return {
      // the person's own device placed them with its filtered anchor at the shutter: reuse it
      srcW: p.image.width, srcH: p.image.height, anchor: p.decision?.anchors?.[idOf(p)] ?? anchorOf(p.pose, p.face), adjust: p.adjust,
      slotX: stageXOf(dispX, mirror), scene: p === scene, noFace: p.noFace, natural: !!d?.natural,
    };
  });
  // same +10 % perspective zoom as live for whoever stood in front
  if (d && !d.natural) {
    const zs = people.map((p) => d.depth[idOf(p)]).filter((z): z is number => z != null);
    if (zs.length > 1) {
      const zMax = Math.max(...zs);
      people.forEach((p, i) => { const z = d.depth[idOf(p)]; if (z != null) inputs[i].extraZoom = 1 + Math.min(0.1, (zMax - z) * 0.5); });
    }
  }
  const placed = placeAll(w, h, inputs, mirror)
    .map((pl, i) => (d?.view && pl.masked ? anchorBottom(applyView(pl, d.view, w, h, inputs[i].srcW, inputs[i].srcH), inputs[i].srcW, inputs[i].srcH, h) : pl));
  const flipM = (m: number[]) => (mirrorPhoto ? [-m[0], m[1], -m[2], m[3], w - m[4], m[5]] : m) as [number, number, number, number, number, number];
  const matrix = (i: number) => flipM(placementMatrix(placed[i]));
  return { d, mirror, mirrorPhoto, scene, others, legacy, inputs, placed, flipM, matrix };
}

/**
 * v0.9.3: where an edge of a person's camera frame lies INSIDE the cell (the frame was moved or
 * shrunk to stand next to someone), the body is cut there by a straight line that gives the
 * composite away. That band fades out instead (6 % of the frame's width / 4 % of its height).
 * `ctx` is already in the person's source-pixel transform.
 */
export function featherFrameEdges(ctx: Ctx2D, pl: Placement, iw: number, ih: number, w: number, h: number) {
  const inside = (pts: [number, number][], test: (p: [number, number]) => boolean) => pts.every(test);
  const L: [number, number][] = [toStage(pl, 0, 0), toStage(pl, 0, ih)];
  const R: [number, number][] = [toStage(pl, iw, 0), toStage(pl, iw, ih)];
  const T: [number, number][] = [toStage(pl, 0, 0), toStage(pl, iw, 0)];
  const B: [number, number][] = [toStage(pl, 0, ih), toStage(pl, iw, ih)];
  const fx = iw * 0.06, fy = ih * 0.04;
  const edges: [boolean, number, number, number, number, number, number, number, number][] = [
    // inside?, gradient x0,y0,x1,y1, rect x,y,w,h
    [inside(L, (p) => p[0] > 2 && p[0] < w - 2), 0, 0, fx, 0, 0, 0, fx, ih],
    [inside(R, (p) => p[0] > 2 && p[0] < w - 2), iw, 0, iw - fx, 0, iw - fx, 0, fx, ih],
    [inside(T, (p) => p[1] > 2 && p[1] < h - 2), 0, 0, 0, fy, 0, 0, iw, fy],
    [inside(B, (p) => p[1] > 2 && p[1] < h - 2), 0, ih, 0, ih - fy, 0, ih - fy, iw, fy],
  ];
  const prev = ctx.globalCompositeOperation;
  ctx.globalCompositeOperation = 'destination-out';
  for (const [on, x0, y0, x1, y1, rx, ry, rw, rh] of edges) {
    if (!on) continue;
    const g = ctx.createLinearGradient(x0, y0, x1, y1);
    g.addColorStop(0, 'rgba(0,0,0,1)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(rx, ry, rw, rh);
  }
  ctx.globalCompositeOperation = prev;
}

/**
 * Draws the people of one shot onto `ctx` (cell size w×h, background already drawn).
 * `sceneSlot`: member whose camera is the backdrop (drawn whole).
 */
export function drawPeople(ctx: Ctx2D, w: number, h: number, people: StillPerson[], sceneSlot: number | null, mirrorPhotoOpt?: boolean) {
  if (!people.length) return;
  const { d, mirror, inputs, placed, flipM, legacy } = shotLayout(w, h, people, sceneSlot, mirrorPhotoOpt);
  const setT = (c: Ctx2D, pl: Placement) => c.setTransform(...flipM(placementMatrix(pl)));
  const setStage = (c: Ctx2D) => c.setTransform(...flipM([1, 0, 0, 1, 0, 0]));


  // backdrop member first
  people.forEach((p, i) => {
    if (placed[i].masked) return;
    ctx.save(); setT(ctx, placed[i]); ctx.drawImage(p.image, 0, 0, p.image.width, p.image.height); ctx.restore();
  });
  const masked = people.map((p, i) => ({ p, pl: placed[i], id: idOf(p), anchor: inputs[i].anchor })).filter((x) => x.pl.masked);
  if (!masked.length) return;

  // decision: captured at the shutter, else solve once from the poses, else flat by slot
  let decision = d;
  if (!decision && masked.some((m) => m.p.pose?.kp)) {
    decision = solveOnce(masked.map((m) => solverPerson(m.id, m.p.pose, m.anchor, m.pl, m.p.image.width, m.p.image.height, h)), {
      ...legacy, mirror,
    }, h).decision;
  }
  const order: LayerRef[] = decision
    ? orderFor(decision, masked.map((m) => m.id))
    : [...masked].sort((a, b) => b.p.slot - a.p.slot).flatMap((m) => LAYER_KINDS.map((kind) => ({ id: m.id, kind })));

  // colour matching on each cut-out (source resolution)
  const sceneIdx = people.findIndex((_, i) => !placed[i].masked);
  const plan = colorPlan(masked.map((m) => m.p.pose?.color), sceneIdx >= 0 ? people[sceneIdx].pose?.color : null);
  const target = plan.target;
  const images = new Map<string, Img>();
  for (const m of masked) {
    const img = m.p.image;
    const x = target && m.p.pose?.color ? reinhardXform(m.p.pose.color, target, plan.strength, plan.chroma) : null;
    if (!x) { images.set(m.id, img); continue; }
    const c = makeCanvas(img.width, img.height);
    const cx = c.getContext('2d')! as Ctx2D;
    cx.drawImage(img, 0, 0);
    const data = cx.getImageData(0, 0, img.width, img.height);
    applyXform(data.data, x);
    cx.putImageData(data, 0, 0);
    images.set(m.id, c as unknown as Img);
  }

  // blurred background for the light wrap (snapshot of what is drawn so far)
  let bgBlur: AnyCanvas | null = null;
  if (sceneSlot == null) {
    bgBlur = makeCanvas(w, h);
    const b = bgBlur.getContext('2d')! as Ctx2D;
    b.filter = `blur(${Math.max(2, Math.round(h * 0.012))}px)`;
    b.drawImage(ctx.canvas as AnyCanvas, 0, 0);
    b.filter = 'none';
  }

  // contact points (cell px, un-mirrored) of hands resting on a partner
  const contacts = (decision?.contacts ?? []).filter((c) => c.kind === 'hand-shoulder').flatMap((c) => {
    const m = masked.find((x) => x.id === c.a);
    if (!m?.p.pose) return [];
    const arms = armsFor(decision, m.id);
    const side = (['left', 'right'] as Side[]).find((s) => arms[s].state === 'front' && arms[s].partner === c.b);
    const hp = side && handPoint(m.p.pose, side, m.p.image.width, m.p.image.height);
    const sw = shoulderWidth(m.p.pose, m.p.image.width, m.p.image.height);
    if (!hp || !sw) return [];
    const [x, y] = toStage(m.pl, hp[0], hp[1]);
    const r = Math.max(6, sw * m.pl.s * 0.32);
    return [{ b: c.b, x: x + r * 0.15, y: y + r * 0.35, r }];
  });

  // ground shadows under visible feet
  for (const m of masked) {
    const p = m.p.pose;
    if (!p?.kp) continue;
    const feet = [KP.leftAnkle, KP.rightAnkle].filter((i) => kpV(p, i) > 0.6)
      .map((i) => toStage(m.pl, kpX(p, i) * m.p.image.width, kpY(p, i) * m.p.image.height));
    if (!feet.length) continue;
    const cx = feet.reduce((a, q) => a + q[0], 0) / feet.length, cy = Math.max(...feet.map((q) => q[1])) + 4;
    const rx = 0.12 * h, ry = 0.025 * h;
    ctx.save(); setStage(ctx);
    ctx.translate(cx, cy); ctx.scale(1, ry / rx);
    const g = ctx.createRadialGradient(0, 0, rx * 0.25, 0, 0, rx);
    g.addColorStop(0, 'rgba(0,0,0,0.28)'); g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g; ctx.beginPath(); ctx.arc(0, 0, rx, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }

  let prev: string | null = null;
  const layer = makeCanvas(w, h), mask = makeCanvas(w, h), wrap = makeCanvas(w, h);
  const lc = layer.getContext('2d')! as Ctx2D, mc = mask.getContext('2d')! as Ctx2D, wc = wrap.getContext('2d')! as Ctx2D;
  for (const ref of order) {
    const m = masked.find((x) => x.id === ref.id);
    if (!m) continue;
    const arms = armsFor(decision, m.id);
    const grp = ref.kind === 'arms_front' ? 'front' : ref.kind === 'arms_back' ? 'back' : null;
    if (grp && !(['left', 'right'] as Side[]).some((s) => arms[s].state === grp)) continue;
    if ((ref.kind === 'head' || ref.kind === 'arms_front') && prev === m.id) continue;
    const img = images.get(m.id)!;
    const iw = m.p.image.width, ih = m.p.image.height;
    lc.setTransform(1, 0, 0, 1, 0, 0); lc.globalCompositeOperation = 'source-over'; lc.clearRect(0, 0, w, h);
    lc.imageSmoothingEnabled = true; lc.imageSmoothingQuality = 'high';
    setT(lc, m.pl);
    lc.drawImage(img, 0, 0, iw, ih);
    featherFrameEdges(lc, m.pl, iw, ih, w, h);

    // part mask (skipped for people without a pose: one flat layer)
    const geos = (['left', 'right'] as Side[]).map((s) => ({ s, st: arms[s].state, g: armGeometry(m.p.pose, s, iw, ih, arms[s].state === 'back') }));
    const capsFor = (st: string) => geos.filter((x) => x.st === st && x.g);
    if (m.p.pose?.kp && ref.kind !== 'body' || capsFor('back').length) {
      mc.setTransform(1, 0, 0, 1, 0, 0); mc.clearRect(0, 0, w, h);
      setT(mc, m.pl);
      mc.fillStyle = mc.strokeStyle = '#fff';
      if (ref.kind === 'head') {
        const e = headEllipse(m.anchor, iw, ih);
        mc.beginPath(); mc.ellipse(e.cx, e.cy, e.rx, e.ry, e.rot, 0, Math.PI * 2); mc.fill();
      } else {
        for (const x of capsFor(ref.kind === 'arms_front' ? 'front' : 'back')) capsulePath(mc, x.g!);
        if (ref.kind === 'body') {
          // cut the arm out of the body only where the partner is (same as the live shader: no seam
          // on the owner's own shoulder; arms_back fills the rest underneath)
          const partners = (['left', 'right'] as Side[]).filter((s) => arms[s].state === 'back').map((s) => arms[s].partner);
          mc.setTransform(1, 0, 0, 1, 0, 0);
          const cut = makeCanvas(w, h), cc = cut.getContext('2d')! as Ctx2D;
          for (const o of masked.filter((x) => partners.includes(x.id))) { setT(cc, o.pl); cc.drawImage(o.p.image, 0, 0, o.p.image.width, o.p.image.height); }
          mc.globalCompositeOperation = 'destination-in';
          mc.drawImage(cut as unknown as Img, 0, 0);
          mc.globalCompositeOperation = 'source-over';
        }
      }
      lc.setTransform(1, 0, 0, 1, 0, 0);
      lc.globalCompositeOperation = ref.kind === 'body' ? 'destination-out' : 'destination-in';
      lc.drawImage(mask as unknown as Img, 0, 0);
      lc.globalCompositeOperation = 'source-over';
    }

    // light wrap: α · (1 − blur(α)) band of the blurred background
    if (bgBlur) {
      wc.setTransform(1, 0, 0, 1, 0, 0); wc.globalCompositeOperation = 'source-over'; wc.clearRect(0, 0, w, h);
      wc.drawImage(bgBlur as unknown as Img, 0, 0);
      wc.globalCompositeOperation = 'destination-in'; wc.drawImage(layer as unknown as Img, 0, 0);
      wc.globalCompositeOperation = 'destination-out';
      wc.filter = `blur(${Math.max(1.5, h * 0.006)}px)`; wc.drawImage(layer as unknown as Img, 0, 0); wc.filter = 'none';
      lc.globalCompositeOperation = 'source-atop'; lc.globalAlpha = WRAP;
      lc.drawImage(wrap as unknown as Img, 0, 0);
      lc.globalAlpha = 1; lc.globalCompositeOperation = 'source-over';
    }

    // soft contact shadow of a partner's hand on this person's body / head
    if (ref.kind === 'body' || ref.kind === 'head') {
      for (const c of contacts.filter((x) => x.b === m.id)) {
        lc.save(); setStage(lc); lc.globalCompositeOperation = 'source-atop';
        const g = lc.createRadialGradient(c.x, c.y, 0, c.x, c.y, c.r * 1.6);
        g.addColorStop(0, `rgba(0,0,0,${CONTACT_SHADOW})`); g.addColorStop(1, 'rgba(0,0,0,0)');
        lc.fillStyle = g; lc.fillRect(c.x - c.r * 2, c.y - c.r * 2, c.r * 4, c.r * 4);
        lc.restore();
      }
    }
    ctx.drawImage(layer as unknown as Img, 0, 0);
    prev = m.id;
  }
}
